import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Checkbox } from 'antd';
import { UserOutlined, ArrowLeftOutlined, SafetyOutlined } from '@ant-design/icons';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import settingsService from '../../services/settingsService';

const CustomerInfoForm = ({ onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [showKvkk, setShowKvkk] = useState(false);
  const [kvkkText, setKvkkText] = useState('');
  const [showAcikRiza, setShowAcikRiza] = useState(false);
  const [acikRizaText, setAcikRizaText] = useState('');

  useEffect(() => {
    const fetchTexts = async () => {
      try {
        const [kvkk, acikRiza] = await Promise.all([
          settingsService.getSettingValue('KVKK_TEXT'),
          settingsService.getSettingValue('ACIKRIZA_TEXT'),
        ]);
        setKvkkText(kvkk);
        setAcikRizaText(acikRiza);
      } catch (error) {
        console.error('Onay metinleri yüklenemedi:', error);
        setKvkkText('KVKK metni yÜklenirken bir hata oluştu.');
        setAcikRizaText('Açık rıza metni yüklenirken bir hata oluştu.');
      }
    };
    fetchTexts();
  }, []);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await onSubmit({
        name: values.name,
        surname: values.surname,
        phone: values.phone,
        kvkkAccepted: values.kvkkAccepted,
        acikRizaAccepted: values.acikRizaAccepted,
      });
    } finally {
      setLoading(false);
    }
  };

  if (showKvkk) {
    return (
      <div className="chat-kvkk-panel">
        <div className="chat-kvkk-header">
          <button className="chat-kvkk-back" onClick={() => setShowKvkk(false)}>
            <ArrowLeftOutlined />
          </button>
          <div className="chat-kvkk-header-info">
            <SafetyOutlined style={{ color: 'var(--chat-primary)', fontSize: 16 }} />
            <span>KVKK Aydınlatma Metni</span>
          </div>
        </div>
        <div className="chat-kvkk-body">
          {kvkkText}
        </div>
        <div className="chat-kvkk-footer">
          <button className="chat-kvkk-back-btn" onClick={() => setShowKvkk(false)}>
            Geri Dön
          </button>
          <button className="chat-kvkk-accept-btn" onClick={() => { form.setFieldValue('kvkkAccepted', true); setShowKvkk(false); }}>
            Okudum, Kabul Ediyorum
          </button>
        </div>
      </div>
    );
  }

  if (showAcikRiza) {
    return (
      <div className="chat-kvkk-panel">
        <div className="chat-kvkk-header">
          <button className="chat-kvkk-back" onClick={() => setShowAcikRiza(false)}>
            <ArrowLeftOutlined />
          </button>
          <div className="chat-kvkk-header-info">
            <SafetyOutlined style={{ color: 'var(--chat-primary)', fontSize: 16 }} />
            <span>Açık Rıza Metni</span>
          </div>
        </div>
        <div className="chat-kvkk-body">
          {acikRizaText}
        </div>
        <div className="chat-kvkk-footer">
          <button className="chat-kvkk-back-btn" onClick={() => setShowAcikRiza(false)}>
            Geri Dön
          </button>
          <button className="chat-kvkk-accept-btn" onClick={() => { form.setFieldValue('acikRizaAccepted', true); setShowAcikRiza(false); }}>
            Okudum, Kabul Ediyorum
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-form-container">
      <div className="chat-form-header">
        <h3>Hoş Geldiniz</h3>
        <p>Size daha iyi yardımcı olabilmemiz icin lütfen bilgilerinizi girin</p>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        style={{ flex: 1 }}
      >
        <Form.Item
          name="name"
          label={<span style={{ fontWeight: 600, fontSize: 13, color: 'var(--chat-text-primary)' }}>Ad</span>}
          rules={[
            { required: true, message: 'Lütfen adınızı girin' },
            { min: 2, message: 'Ad en az 2 karakter olmalı' },
          ]}
        >
          <Input
            prefix={<UserOutlined style={{ color: 'var(--chat-text-tertiary)' }} />}
            placeholder="Adınız"
            size="large"
            style={{ background: 'var(--chat-surface-secondary)' }}
          />
        </Form.Item>

        <Form.Item
          name="surname"
          label={<span style={{ fontWeight: 600, fontSize: 13, color: 'var(--chat-text-primary)' }}>Soyad</span>}
          rules={[
            { required: true, message: 'Lütfen soyadınızı girin' },
            { min: 2, message: 'Soyad en az 2 karakter olmalı' },
          ]}
        >
          <Input
            prefix={<UserOutlined style={{ color: 'var(--chat-text-tertiary)' }} />}
            placeholder="Soyadınız"
            size="large"
            style={{ background: 'var(--chat-surface-secondary)' }}
          />
        </Form.Item>

        <Form.Item
          name="phone"
          label={<span style={{ fontWeight: 600, fontSize: 13, color: 'var(--chat-text-primary)' }}>Telefon Numarası</span>}
          rules={[
            { required: true, message: 'Lütfen telefon numaranızı girin' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (isValidPhoneNumber(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Geçerli bir telefon numarası girin'));
              },
            },
          ]}
        >
          <PhoneInput
            international
            defaultCountry="TR"
            placeholder="Telefon numaranız"
            className="chat-phone-input"
          />
        </Form.Item>

        <Form.Item
          name="kvkkAccepted"
          valuePropName="checked"
          style={{ marginBottom: 0 }}
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error('KVKK metnini kabul etmelisiniz')),
            },
          ]}
        >
          <Checkbox>
            <span style={{ fontSize: 12, color: 'var(--chat-text-secondary)' }}>
              <a
                onClick={(e) => { e.preventDefault(); setShowKvkk(true); }}
                style={{ color: 'var(--chat-primary)', fontWeight: 600, cursor: 'pointer' }}
              >
                KVKK Aydınlatma Metni
              </a>
              'ni okudum ve kabul ediyorum.
            </span>
          </Checkbox>
        </Form.Item>

        <Form.Item
          name="acikRizaAccepted"
          valuePropName="checked"
          style={{ marginBottom: 4 }}
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error('Açık rıza metnini kabul etmelisiniz')),
            },
          ]}
        >
          <Checkbox>
            <span style={{ fontSize: 12, color: 'var(--chat-text-secondary)' }}>
              <a
                onClick={(e) => { e.preventDefault(); setShowAcikRiza(true); }}
                style={{ color: 'var(--chat-primary)', fontWeight: 600, cursor: 'pointer' }}
              >
                Açık Rıza Metni
              </a>
              'ni okudum ve kabul ediyorum.
            </span>
          </Checkbox>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            block
            className="chat-form-submit"
          >
            Sohbeti Başlat
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CustomerInfoForm;
